import { useState } from 'react';
import { Box, Collapse, Stack, useTheme } from '@mui/material';
import { Grid } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import { ShadowButton } from '../shadow-button';
import { LandingSectionTitle } from '../landing-section-title';
import { PrevNextBtns } from '../prev-next-btns';
import MainPromo from './main-promo';
import SecondaryPromo from './secondary-promo';
import { BoxAbsolute } from './styles';
import { TPromotion } from '../../types/config';
import { useIsMobile } from '../../hooks';

export type PromotionsProps = {
  data: TPromotion[];
  projectId?: number;
  onBigPromotionClick?: (data: TPromotion) => void;
  onSmallPromotionClick?: (data: TPromotion) => void;
  t: (key: string) => string;
};

export const SECONDARY_PROMO_HEIGHT = 262;
const COLLAPSED_PROMOS_HEIGHT = SECONDARY_PROMO_HEIGHT + 10;

export function Promotions({
  data,
  onBigPromotionClick,
  onSmallPromotionClick,
  t,
}: PromotionsProps) {
  const isMobile = useIsMobile();
  const { direction } = useTheme();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    slidesToScroll: 'auto',
    containScroll: false,
    direction,
  });

  const [isCollapsed, setIsCollapsed] = useState(true);

  return (
    <>
      <Stack direction="column">
        <LandingSectionTitle
          title={t('labels.promotions')}
          subtitle={t('info.promotionsDescription')}
          sx={{ padding: { xs: '0 16px', sm: '0' } }}
        />

        {!isMobile ? (
          <>
            {data[0] && (
              <>
                <MainPromo
                  data={data[0]}
                  onClick={() => {
                    onBigPromotionClick && onBigPromotionClick(data[0]);
                  }}
                />
              </>
            )}

            {data.length > 1 && (
              <>
                <Collapse in={!isCollapsed} collapsedSize={COLLAPSED_PROMOS_HEIGHT}>
                  <Stack
                    spacing="10px"
                    direction="row"
                    justifyContent="space-between"
                    alignItems="stretch"
                    alignContent="stretch"
                  >
                    {data.slice(1, 5).map((promo, index) => (
                      <SecondaryPromo
                        key={index}
                        {...promo}
                        stretch
                        onClick={() => {
                          onSmallPromotionClick && onSmallPromotionClick(promo);
                        }}
                      />
                    ))}
                  </Stack>

                  {data.length > 5 && (
                    <Grid
                      container
                      direction="row"
                      columns={16}
                      spacing="10px"
                      sx={{
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      {data.slice(5, 9).map((promo, index) => (
                        <Grid key={index} size={{ xs: 4 }}>
                          <SecondaryPromo
                            {...promo}
                            stretch={false}
                            onClick={() => {
                              onSmallPromotionClick && onSmallPromotionClick(promo);
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Collapse>

                {data.length > 5 && (
                  <ShadowButton
                    label={isCollapsed ? t('labels.readMore') : t('labels.readLess')}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Stack direction="column" spacing="20px" mt="20px">
              {data.length > 1 && (
                <PrevNextBtns
                  onPrev={() => emblaApi?.scrollPrev()}
                  onNext={() => emblaApi?.scrollNext()}
                  sx={{ px: { xs: '16px', sm: '0' } }}
                />
              )}

              <Box overflow="hidden" ref={emblaRef}>
                {/* Embla container */}
                <Stack direction="row">
                  {data.map((promo, index) => (
                    <Box
                      key={index}
                      sx={{
                        mx: 1,
                        display: 'block',
                        position: 'relative',
                        flex: {
                          xs: '0 0 calc(100% - 32px)',
                          sm: '0 0 48%',
                          md: '0 0 32%',
                          lg: '0 0 24%',
                        },
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        onSmallPromotionClick && onSmallPromotionClick(promo);
                      }}
                    >
                      <Box sx={{ position: 'relative', width: '100%', height: 'auto', zIndex: 1 }}>
                        {promo.bgImage ? (
                          <img
                            src={promo.bgImage || ''}
                            style={{ width: '100%', borderRadius: '8px' }}
                          />
                        ) : (
                          <BoxAbsolute
                            sx={{
                              zIndex: 1,
                              backgroundColor: promo.bgColor || '#000',
                              borderRadius: '8px',
                            }}
                          />
                        )}

                        <BoxAbsolute
                          sx={{
                            zIndex: 2,
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.1)',
                            },
                          }}
                        />
                      </Box>

                      <ShadowButton
                        label={t('labels.learnMore')}
                        onClick={() => {
                          onSmallPromotionClick && onSmallPromotionClick(promo);
                        }}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </>
  );
}
